/**
 * Single source of truth for page metadata.
 *
 * Everything here is pure: build a descriptor, then either apply it to the DOM
 * (`applyMeta`, used by the pages at runtime) or serialise it to an HTML string
 * (`renderHead`, used by scripts/prerender.js at build time). Both paths must
 * produce the same tags, which is the whole point of keeping them together.
 *
 * Imported by Node (the prerender and sitemap scripts) as well as the browser,
 * so this file must not touch the DOM at module scope.
 */

export const SITE_URL = 'https://talkmuch.org';

export const SITE_NAME = 'TalkMuch';

export const DEFAULT_TITLE = 'TalkMuch';

export const DEFAULT_DESCRIPTION =
  'TalkMuch — Who does the talking? See exactly how much your favourite host speaks in every episode.';

/**
 * Convert a title string to a URL-safe slug.
 * e.g. "My Episode Title! #42" -> "my-episode-title-42"
 *
 * Canonical definition. api.js re-exports it and scripts/generate-sitemap.js
 * imports it, so episode URLs cannot drift between the app and the sitemap.
 *
 * @param {string} title
 * @returns {string}
 */
export function slugify(title) {
  return (title ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function canonicalUrl(path = '/') {
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Collapse whitespace and clip to a length search engines will actually show. */
function clip(text, max = 160) {
  const flat = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  return `${flat.slice(0, max - 1).trimEnd()}…`;
}

/* ────────────────────────── descriptors ────────────────────────── */

/**
 * @typedef {object} Meta
 * @property {string} title
 * @property {string} description
 * @property {string} canonical   - absolute URL
 * @property {object} [og]        - extra OpenGraph properties beyond the defaults
 * @property {object} [jsonLd]    - schema.org graph, omitted when absent
 */

export function landingMeta() {
  return {
    title: DEFAULT_TITLE,
    description:
      'Every podcast episode, scored by who talks more. See the host-versus-guest speaking split so you can choose before you play.',
    canonical: canonicalUrl('/'),
    og: { 'og:title': 'TalkMuch: who really runs the show?' },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: canonicalUrl('/'),
    },
  };
}

export function homeMeta() {
  return {
    title: 'Podcasts | TalkMuch',
    description:
      'Browse every podcast on TalkMuch and see how much each host talks, episode by episode.',
    canonical: canonicalUrl('/podcasts'),
    og: { 'og:title': 'Browse podcasts | TalkMuch — Podcast host talk stats' },
  };
}

export function leaderboardMeta() {
  return {
    title: 'Leaderboard | TalkMuch',
    description:
      'The episodes at the extremes: biggest host monologues, most lopsided talk splits, and the guests who barely got a word in.',
    canonical: canonicalUrl('/leaderboard'),
    og: { 'og:title': 'Podcast talk-time leaderboard | TalkMuch' },
  };
}

export function podcastMeta(podcast) {
  const name = podcast?.name ?? '';
  const slug = podcast?.podcast_uuid ?? '';
  const url = canonicalUrl(`/podcast/${slug}`);
  return {
    title: `${name} | TalkMuch`,
    description: `${name} — see how much ${podcast?.host_name ?? 'the host'} talks in every episode. Host talk stats on TalkMuch.`,
    canonical: url,
    og: {
      'og:title': `${name} | TalkMuch`,
      ...(podcast?.artwork_url ? { 'og:image': podcast.artwork_url } : {}),
    },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'PodcastSeries',
      name,
      url,
      ...(podcast?.artwork_url ? { image: podcast.artwork_url } : {}),
      ...(podcast?.host_name ? { author: { '@type': 'Person', name: podcast.host_name } } : {}),
    },
  };
}

export function episodeMeta(episode, podcast) {
  const podcastName = podcast?.name ?? '';
  const title = episode?.title ?? '';
  const slug = podcast?.podcast_uuid ?? episode?.podcast_uuid ?? '';
  const url = canonicalUrl(`/podcast/${slug}/episode/${slugify(title)}`);

  const hostPct = episode?.host_pct != null ? episode.host_pct.toFixed(1) : null;
  const guestPct = episode?.guest_pct != null ? episode.guest_pct.toFixed(1) : null;

  // Lead with the stat, which is the original contribution, then fall back to
  // the publisher's own summary only when we have no numbers to show.
  const description = hostPct != null
    ? `${podcast?.host_name ?? 'The host'} talked ${hostPct}% of the time, the guest ${guestPct}%. Talk-time breakdown for "${title}" on ${podcastName}.`
    : clip(episode?.description) || `${title} — ${podcastName} on TalkMuch`;

  return {
    title: `${title} — ${podcastName} | TalkMuch`,
    description,
    canonical: url,
    og: {
      'og:title': `${title} — ${podcastName} host stats | TalkMuch`,
      ...(podcast?.artwork_url ? { 'og:image': podcast.artwork_url } : {}),
    },
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'PodcastEpisode',
          name: title,
          description,
          url,
          ...(episode?.published_at ? { datePublished: episode.published_at } : {}),
          partOfSeries: { '@type': 'PodcastSeries', name: podcastName, url: canonicalUrl(`/podcast/${slug}`) },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: canonicalUrl('/') },
            { '@type': 'ListItem', position: 2, name: podcastName, item: canonicalUrl(`/podcast/${slug}`) },
            { '@type': 'ListItem', position: 3, name: title, item: url },
          ],
        },
      ],
    },
  };
}

/** About / Contact / Privacy / Methodology / analysis articles. */
export function pageMeta({ path, title, description, type = 'website', jsonLd }) {
  return {
    title: `${title} | TalkMuch`,
    description,
    canonical: canonicalUrl(path),
    og: { 'og:title': `${title} | TalkMuch`, 'og:type': type },
    jsonLd,
  };
}

/* ────────────────────────── browser applier ────────────────────────── */

function upsertMeta(selector, create) {
  let tag = document.head.querySelector(selector);
  if (tag) return { tag, created: false };
  tag = create();
  document.head.appendChild(tag);
  return { tag, created: true };
}

/**
 * Apply a descriptor to document.head. Returns a cleanup function that removes
 * anything it created and restores the defaults, so pages can call it straight
 * from a `useEffect`.
 *
 * @param {Meta} meta
 * @returns {() => void}
 */
export function applyMeta(meta) {
  if (!meta) return () => {};
  const undo = [];

  document.title = meta.title ?? DEFAULT_TITLE;

  const desc = upsertMeta('meta[name="description"]', () => {
    const el = document.createElement('meta');
    el.setAttribute('name', 'description');
    return el;
  });
  desc.tag.setAttribute('content', meta.description ?? DEFAULT_DESCRIPTION);

  const canonical = upsertMeta('link[rel="canonical"]', () => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    return el;
  });
  canonical.tag.setAttribute('href', meta.canonical ?? canonicalUrl('/'));
  if (canonical.created) undo.push(() => canonical.tag.remove());

  const og = {
    'og:title': meta.title,
    'og:description': meta.description,
    'og:url': meta.canonical,
    'og:type': 'website',
    'og:site_name': SITE_NAME,
    ...(meta.og ?? {}),
  };
  for (const [property, content] of Object.entries(og)) {
    if (content == null) continue;
    const { tag, created } = upsertMeta(`meta[property="${property}"]`, () => {
      const el = document.createElement('meta');
      el.setAttribute('property', property);
      return el;
    });
    tag.setAttribute('content', content);
    if (created) undo.push(() => tag.remove());
  }

  if (meta.jsonLd) {
    const { tag, created } = upsertMeta('script[data-tm-jsonld]', () => {
      const el = document.createElement('script');
      el.setAttribute('type', 'application/ld+json');
      el.setAttribute('data-tm-jsonld', '');
      return el;
    });
    tag.textContent = JSON.stringify(meta.jsonLd);
    if (created) undo.push(() => tag.remove());
  }

  return () => {
    document.title = DEFAULT_TITLE;
    desc.tag.setAttribute('content', DEFAULT_DESCRIPTION);
    undo.forEach(fn => fn());
  };
}

/* ────────────────────────── prerender serialiser ────────────────────────── */

function escapeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Serialise a descriptor to the `<head>` fragment scripts/prerender.js injects.
 *
 * Episode descriptions come from publisher RSS feeds and arrive with raw
 * entities and curly quotes, so every interpolated value is escaped.
 *
 * @param {Meta} meta
 * @returns {string} HTML
 */
export function renderHead(meta) {
  const og = {
    'og:title': meta.title,
    'og:description': meta.description,
    'og:url': meta.canonical,
    'og:type': 'website',
    'og:site_name': SITE_NAME,
    ...(meta.og ?? {}),
  };

  const lines = [
    `<title>${escapeAttr(meta.title ?? DEFAULT_TITLE)}</title>`,
    `<meta name="description" content="${escapeAttr(meta.description ?? DEFAULT_DESCRIPTION)}" />`,
  ];

  // A canonical URL on an error page tells a crawler the page is a legitimate
  // copy of whatever it points at, which is the soft-404 signal we are trying
  // to avoid. Error pages get `noindex` and no canonical instead.
  if (meta.noindex) {
    lines.push(`<meta name="robots" content="noindex" />`);
  } else {
    lines.push(`<link rel="canonical" href="${escapeAttr(meta.canonical ?? canonicalUrl('/'))}" />`);
  }

  for (const [property, content] of Object.entries(og)) {
    if (content == null) continue;
    lines.push(`<meta property="${escapeAttr(property)}" content="${escapeAttr(content)}" />`);
  }

  lines.push(`<meta name="twitter:card" content="summary_large_image" />`);

  if (meta.jsonLd) {
    // `</script>` inside JSON would close the tag early; `<` escaping prevents it.
    const json = JSON.stringify(meta.jsonLd).replace(/</g, '\\u003c');
    lines.push(`<script type="application/ld+json" data-tm-jsonld>${json}</script>`);
  }

  return lines.join('\n    ');
}
